import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import styles from './Payments.module.css';

const REQUIRED_READING_SECONDS = 10;

const StripeOnboardingNoticeModal = ({
  isOpen,
  onClose,
  onContinue,
  isSubmitting = false
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(REQUIRED_READING_SECONDS);

  useEffect(() => {
    if (!isOpen) {
      setSecondsRemaining(REQUIRED_READING_SECONDS);
      return undefined;
    }

    setSecondsRemaining(REQUIRED_READING_SECONDS);

    const intervalId = window.setInterval(() => {
      setSecondsRemaining((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen]);

  if (!isOpen) return null;

  const canContinue = secondsRemaining === 0 && !isSubmitting;

  return (
    <div className={styles.noticeModalOverlay} onClick={isSubmitting ? undefined : onClose}>
      <div
        className={styles.noticeModal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="stripe-onboarding-notice-title"
      >
        <button
          type="button"
          className={styles.noticeModalCloseButton}
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close Stripe setup notice"
        >
          <XMarkIcon className={styles.noticeModalCloseIcon} />
        </button>

        <div className={styles.noticeModalIconWrapper}>
          <ExclamationTriangleIcon className={styles.noticeModalIcon} />
        </div>

        <p className={styles.noticeModalEyebrow}>Important before you continue</p>
        <h3 id="stripe-onboarding-notice-title" className={styles.noticeModalTitle}>
          Create a brand new Stripe account for Boutee only
        </h3>

        <p className={styles.noticeModalDescription}>
          When Stripe asks whether you want to link an existing account or create a new one,
          choose <strong>create a new account</strong>.
        </p>

        <div className={styles.noticeModalCallout}>
          <p className={styles.noticeModalCalloutTitle}>Why this matters</p>
          <ul className={styles.noticeModalList}>
            <li>Use a separate Stripe account just for Boutee invoices, payments, and payouts.</li>
            <li>Do not connect an existing Stripe account that you already use outside Boutee.</li>
            <li>This keeps Boutee activity separate from your other Stripe sales and reporting.</li>
          </ul>
        </div>

        <p className={styles.noticeModalFooterText}>
          Please take a moment to read this before Stripe opens.
        </p>

        <div className={styles.noticeModalActions}>
          <button
            type="button"
            className={styles.noticeModalSecondaryButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.noticeModalPrimaryButton}
            onClick={onContinue}
            disabled={!canContinue}
          >
            {isSubmitting
              ? 'Opening Stripe...'
              : secondsRemaining > 0
                ? `Continue to Stripe (${secondsRemaining}s)`
                : 'Continue to Stripe'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeOnboardingNoticeModal;
